
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export interface UserReview {
  id: string;
  reviewer_id: string;
  reviewed_user_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
  reviewer?: {
    full_name?: string;
    username?: string;
    profile_image?: string;
  };
}

export const useUserReview = (userId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reviewFormOpen, setReviewFormOpen] = useState(false);

  // Check if current user has already reviewed this user
  const { data: hasReviewed, isLoading: checkingReview } = useQuery({
    queryKey: ['hasReviewed', userId, user?.id],
    queryFn: async () => {
      if (!userId || !user) return false;
      
      try {
        // Use the rpc function via raw SQL query as a workaround for TypeScript errors
        const { data, error } = await supabase
          .rpc('has_user_reviewed', {
            reviewer: user.id,
            reviewed: userId
          } as any);
          
        if (error) throw error;
        console.log('Has user reviewed result:', data);
        return data || false;
      } catch (error) {
        console.error('Error checking review status:', error);
        return false;
      }
    },
    enabled: !!userId && !!user,
  });

  // Get user's existing review if any
  const { data: existingReview, isLoading: loadingReview } = useQuery({
    queryKey: ['userReview', userId, user?.id],
    queryFn: async () => {
      if (!userId || !user || !hasReviewed) return null;
      
      const { data, error } = await supabase
        .from('user_reviews')
        .select('*')
        .eq('reviewer_id', user.id)
        .eq('reviewed_user_id', userId)
        .single();
        
      if (error) {
        console.error('Error fetching review:', error);
        return null;
      }
      
      console.log('Existing review:', data);
      return data as UserReview;
    },
    enabled: !!userId && !!user && !!hasReviewed,
  });

  // Get all reviews for a user - FIXED QUERY
  const { data: userReviews, isLoading: loadingReviews } = useQuery({
    queryKey: ['userReviews', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      console.log('Fetching reviews for user:', userId);
      
      // First fetch the reviews
      const { data: reviews, error } = await supabase
        .from('user_reviews')
        .select('*')
        .eq('reviewed_user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error fetching reviews:', error);
        return [];
      }
      
      // Then get the reviewer profiles for each review
      const enhancedReviews = await Promise.all(
        reviews.map(async (review) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name, username, profile_image')
            .eq('id', review.reviewer_id)
            .single();
            
          return {
            ...review,
            reviewer: profileData || {
              full_name: undefined,
              username: undefined,
              profile_image: undefined
            }
          };
        })
      );
      
      console.log('Enhanced user reviews:', enhancedReviews);
      return enhancedReviews as UserReview[];
    },
    enabled: !!userId,
  });

  // Get average rating for a user
  const { data: averageRating = 0 } = useQuery({
    queryKey: ['userAverageRating', userId],
    queryFn: async () => {
      if (!userId) return 0;
      
      // Use the rpc function via raw SQL query as a workaround for TypeScript errors
      const { data, error } = await supabase
        .rpc('get_user_average_rating', {
          user_id: userId
        } as any);
        
      if (error) {
        console.error('Error fetching average rating:', error);
        return 0;
      }
      
      return Number(data) || 0;
    },
    enabled: !!userId,
  });

  // Submit a review
  const submitReviewMutation = useMutation({
    mutationFn: async ({ rating, comment }: { rating: number; comment?: string }) => {
      if (!userId || !user) throw new Error('Missing user information');
      
      // Validate rating
      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }
      
      console.log('Submitting review:', { rating, comment, existingReview });
      
      if (existingReview) {
        // Update existing review
        const { error } = await supabase
          .from('user_reviews')
          .update({
            rating,
            comment: comment || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingReview.id);
          
        if (error) throw error;
      } else {
        // Insert new review
        const { error } = await supabase
          .from('user_reviews')
          .insert({
            reviewer_id: user.id,
            reviewed_user_id: userId,
            rating,
            comment: comment || null
          });
          
        if (error) throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['userReviews', userId] });
      queryClient.invalidateQueries({ queryKey: ['hasReviewed', userId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userReview', userId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userAverageRating', userId] });
      
      toast({
        title: existingReview ? 'Review updated' : 'Review submitted',
        description: existingReview 
          ? 'Your review has been updated successfully.' 
          : 'Thank you for submitting your review!',
      });
      
      setReviewFormOpen(false);
    },
    onError: (error) => {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to submit review.',
        variant: 'destructive',
      });
    },
  });

  return {
    hasReviewed,
    existingReview,
    userReviews,
    averageRating,
    isLoading: checkingReview || loadingReview || loadingReviews,
    submitReview: submitReviewMutation.mutate,
    isSubmitting: submitReviewMutation.isPending,
    reviewFormOpen,
    setReviewFormOpen,
  };
};
